import m from 'mithril'

import {
	USER_MANAGEMENT_SOLUTION_TYPE,
	CUSTOMER_MODE_DEFAULTS,
	USER_MANAGEMENT_MODULE_TYPE,
	USER_MANAGEMENT_FEATURE_TYPE,
	DATE_RANGE_OPTIONS,
} from '@shared/constants'
import client from '@shared/utils/client-rest'
import router from '@app/router'
import { USER_MANAGEMENT_ROLE_TYPE } from '@shared/constants'
import { notifications } from '@client-shared/components-v2/toast' 
import { maximizeID } from '@shared/utils/uuid-compression'
import { getFrequencyDefault } from '@app/helpers/utils'
import { SUUID } from 'short-uuid'
import { ILitmusCustomProjectType } from '@shared-types/reports'
import { DEFAULT_LITMUS_COST_PARAMETERS } from '@shared-schemas/metrics-abacus-litmus.js'
import type { ILitmusCostParameters } from '@shared-schemas/metrics-abacus-litmus.js'
import type { IMetricKey } from '@shared-schemas/metrics-io.constants'
import type { ILitmusMetricCollection } from '@app/components/report-litmus-v2/report-litmus-v2.constants.ts'

export type CerberusDefaultFilters = Record<string, Record<string, string | string[]>>
export interface ICerberusColumnConfig {
	[key: string]: {
		key: string
		display: boolean
		order: number
		width: number
		fixed?: boolean
	}[]
}

interface FunctionalFeature {
	name_slug: string
	[key: string]: unknown
}

interface FunctionalModule {
	name_slug: string
	functional_features?: FunctionalFeature[]
	[key: string]: unknown
}

interface FunctionalSolution {
	name_slug: string
	functional_modules?: FunctionalModule[]
	[key: string]: unknown
}

interface FunctionalRole {
	role: string
	functional_solutions?: FunctionalSolution[]
	[key: string]: unknown
}

interface MeResponse {
	user_id?: string
	email?: string
	given_name?: string
	family_name?: string
	functional_role?: FunctionalRole
	customer_ids?: string[]
	region_ids?: string[]
	metrics_availability: Record<string, boolean>
	api_access?: boolean
	agent_access?: boolean
	feature_flags: string[]
	[key: string]: unknown
}

interface FrequencySendOn {
	date_of_year: number | null
	day_of_weeks: number[]
	week_of_month: number | null
	week_of_quarter: number | null
}

interface Frequency {
	send_at: string
	send_on: FrequencySendOn
	repeat_type: string
}

export const DateRangeOptionType = {
	absolute: 'absolute',
	relative: 'relative',
} as const

export interface DateRangeOption {
	title: string

	// Preset if true
	is_df?: boolean

	// Custom
	id?: SUUID

	// Relative
	key?: number

	// Absolute
	start_date?: string //'YYYY-MM-DD'
	end_date?: string //'YYYY-MM-DD'
}

export interface UserSetting {
	id: string | null
	date_ranges: DateRangeOption[]
	emails: string[]
	frequency: Frequency
	failed_emails: string[]
	cerberus_default_filters: CerberusDefaultFilters
	cerberus_column_config: ICerberusColumnConfig
	litmus_project_types: ILitmusCustomProjectType[]
	litmus_cost_parameters: ILitmusCostParameters
	// Per entity type (metric collection), the user's saved default Litmus metric selection:
	// the selected metric keys, in the user's chosen display order. Used when the URL carries no
	// lit_met_sel_* query param for the entity type.
	litmus_metric_selections: Partial<Record<ILitmusMetricCollection, IMetricKey[]>>
}

export interface UserSettingResponse {
	id?: string
	setting?: {
		date_ranges?: DateRangeOption[]
		trend_email_setting?: {
			frequency?: Frequency
			emails?: string[]
			failed_emails?: string[]
		}
		cerberus_default_filters?: CerberusDefaultFilters
		cerberus_column_config?: ICerberusColumnConfig
		// v1 settings persisted litmus_project_types as string[] — may still come back from the API as-is
		litmus_project_types?: (ILitmusCustomProjectType | string)[]
		litmus_cost_parameters?: ILitmusCostParameters
		litmus_metric_selections?: Partial<Record<ILitmusMetricCollection, IMetricKey[]>>
	}
	[key: string]: unknown
}

interface UpdateUserSettingBody {
	customer_id?: string
	setting?: {
		date_ranges?: DateRangeOption[]
		cerberus_default_filters?: CerberusDefaultFilters
		cerberus_column_config?: ICerberusColumnConfig
		litmus_project_types?: ILitmusCustomProjectType[]
		litmus_cost_parameters?: ILitmusCostParameters
		litmus_metric_selections?: Partial<Record<ILitmusMetricCollection, IMetricKey[]>>
		[key: string]: unknown
	}
	[key: string]: unknown
}

interface PermittedRegion {
	subregions?: unknown[]
	networks?: unknown[]
	[key: string]: unknown
}

type AppMode = 'sigops' | 'safety' | 'mobility'

// Matches client-frontend/router.js MODES values, persisted on a litmus report's report_metadata.mode.
const LITMUS_MODE_TO_SOLUTION: Record<string, string> = {
	sigops: USER_MANAGEMENT_SOLUTION_TYPE.SIGNAL,
	safety: USER_MANAGEMENT_SOLUTION_TYPE.SAFETY,
	mobility: USER_MANAGEMENT_SOLUTION_TYPE.MOBILITY,
}

// v1 settings persisted litmus_project_types as string[] — wrap any legacy string entries into
// the v2 object shape so a future save persists only the v2 structure.
const migrateLitmusProjectTypes = (
	project_types: (ILitmusCustomProjectType | string)[] | undefined,
): ILitmusCustomProjectType[] => {
	return (project_types ?? []).map((project_type) =>
		typeof project_type === 'string' ? { label: project_type, modes: ['sigops'] } : project_type,
	)
}

class Cache {
	private readonly fetch_fresh_ms = 60_000
	private fetch_promises: Map<string, Promise<unknown[]>>
	private user_promise: Promise<unknown> | null
	private user_preferences_promise: Promise<unknown> | null
	private me_promises: Map<string, Promise<MeResponse | void>>
	private user_settings_promises: Map<string, Promise<void>>
	private user_fetched_at: number
	private user_preferences_fetched_at: number
	private me_fetched_at_by_key: Map<string, number>
	private user_settings_fetched_at_by_key: Map<string, number>

	user_preferences_loading: boolean
	user_preferences: unknown

	user_loading: boolean
	user: unknown

	permission_loading: boolean
	permission: FunctionalRole | null

	user_setting_loading: boolean
	user_setting_processing: boolean
	user_setting: UserSetting

	me: MeResponse | null
	is_admin: boolean
	is_super_admin: boolean
	has_api_access: boolean
	has_agent_access: boolean

	permitted_regions: { region: PermittedRegion[]; subregion: unknown[]; network: unknown[] } | null
	loading_permitted_regions?: boolean

	available_modes: AppMode[]
	default_mode: AppMode | null

	constructor() {
		this.fetch_promises = new Map()
		this.user_promise = null
		this.user_preferences_promise = null
		this.me_promises = new Map()
		this.user_settings_promises = new Map()
		this.user_fetched_at = 0
		this.user_preferences_fetched_at = 0
		this.me_fetched_at_by_key = new Map()
		this.user_settings_fetched_at_by_key = new Map()

		this.user_preferences_loading = true
		this.user_preferences = null

		this.user_loading = true
		this.user = null

		this.permission_loading = true
		this.permission = null

		this.user_setting_loading = true
		this.user_setting_processing = false
		this.user_setting = {
			id: null,
			date_ranges: [],
			emails: [],
			frequency: { ...getFrequencyDefault() },
			failed_emails: [],
			cerberus_default_filters: {},
			cerberus_column_config: {},
			litmus_project_types: [],
			litmus_cost_parameters: { ...DEFAULT_LITMUS_COST_PARAMETERS },
			litmus_metric_selections: {},
		}

		this.me = null
		this.is_admin = false
		this.is_super_admin = false
		this.has_api_access = false
		this.has_agent_access = false

		this.permitted_regions = null

		this.available_modes = []
		this.default_mode = null

		if (typeof window !== 'undefined') {
			window.addEventListener('flowlabs:signout', this.clearSessionState)
		}
	}

	private getCacheKey = (customer_id?: string) => customer_id || '__default__'

	private isFresh = (fetched_at?: number) => {
		return !!fetched_at && Date.now() - fetched_at < this.fetch_fresh_ms
	}

	private isBaseFetchFresh = (customer_id?: string) =>
		!!this.user &&
		!!this.user_preferences &&
		!!this.me &&
		this.isFresh(this.user_fetched_at) &&
		this.isFresh(this.user_preferences_fetched_at) &&
		this.isFresh(this.me_fetched_at_by_key.get(this.getCacheKey(customer_id)))

	clearSessionState = () => {
		this.fetch_promises.clear()
		this.user_promise = null
		this.user_preferences_promise = null
		this.me_promises.clear()
		this.user_settings_promises.clear()
		this.user_fetched_at = 0
		this.user_preferences_fetched_at = 0
		this.me_fetched_at_by_key.clear()
		this.user_settings_fetched_at_by_key.clear()

		this.user_preferences_loading = true
		this.user_preferences = null
		this.user_loading = true
		this.user = null
		this.permission_loading = true
		this.permission = null
		this.user_setting_loading = true
		this.user_setting_processing = false
		this.user_setting = {
			id: null,
			date_ranges: [],
			emails: [],
			frequency: { ...getFrequencyDefault() },
			failed_emails: [],
			cerberus_default_filters: {},
			cerberus_column_config: {},
			litmus_project_types: [],
			litmus_cost_parameters: { ...DEFAULT_LITMUS_COST_PARAMETERS },
			litmus_metric_selections: {},
		}
		this.me = null
		this.is_admin = false
		this.is_super_admin = false
		this.has_api_access = false
		this.has_agent_access = false
		this.permitted_regions = null
		this.available_modes = []
		this.default_mode = null
	}

	fetch = async (options: { force?: boolean; customer_id?: string } = {}) => {
		const { force = false, customer_id } = options
		const key = this.getCacheKey(customer_id)

		if (!force && this.isBaseFetchFresh(customer_id)) {
			return [this.user, this.user_preferences, this.me]
		}

		if (!force && this.fetch_promises.has(key)) {
			return await this.fetch_promises.get(key)
		}

		const promise = Promise.all([
			this.fetchUser({ force }),
			this.fetchUserPreferences({ force }),
			this.fetchMe(customer_id, { force }),
		]).finally(() => {
			this.fetch_promises.delete(key)
		})

		this.fetch_promises.set(key, promise)

		return await promise
	}

	fetchUserPreferences = async (options: { force?: boolean } = {}) => {
		const { force = false } = options

		if (!force && this.user_preferences && this.isFresh(this.user_preferences_fetched_at)) {
			return this.user_preferences
		}

		if (!force && this.user_preferences_promise) return this.user_preferences_promise

		this.user_preferences_promise = client('/api/v1/pages/user-managements/user-preferences/me')
			.then(async (user_preferences: unknown) => {
				this.user_preferences = user_preferences
				this.user_preferences_fetched_at = Date.now()
				this.user_preferences_loading = false
				return user_preferences
			})
			.catch((err: Error) => console.error(err?.message === '[object Object]' ? JSON.stringify(err) : err))
			.finally(() => {
				this.user_preferences_promise = null
			})

		return await this.user_preferences_promise
	}

	fetchUser = async (options: { force?: boolean } = {}) => {
		const { force = false } = options

		if (!force && this.user && this.isFresh(this.user_fetched_at)) {
			return this.user
		}

		if (!force && this.user_promise) return this.user_promise

		this.user_promise = client('/api/v1/pages/user')
			.then(async (user: unknown) => {
				this.user = user
				this.user_fetched_at = Date.now()
				this.user_loading = false
				return user
			})
			.catch((err: Error) => console.error(err?.message === '[object Object]' ? JSON.stringify(err) : err))
			.finally(() => {
				this.user_promise = null
			})

		return await this.user_promise
	}

	fetchUserSettings = async (customer_id?: string, options: { force?: boolean } = {}) => {
		const { force = false } = options
		const key = this.getCacheKey(customer_id)

		if (!force && !this.user_setting_loading && this.isFresh(this.user_settings_fetched_at_by_key.get(key))) {
			return
		}

		if (!force && this.user_settings_promises.has(key)) {
			return await this.user_settings_promises.get(key)
		}

		this.user_setting_loading = true

		const promise = client('/api/v1/pages/user-managements/user-settings/current', { params: { customer_id } })
			.then((res: UserSettingResponse) => {
				if (res?.id) {
					this.user_setting.id = res.id
				}
				this.user_setting.date_ranges = res?.setting?.date_ranges ?? []
				this.user_setting.frequency = res?.setting?.trend_email_setting?.frequency ?? { ...getFrequencyDefault() }
				this.user_setting.emails = res?.setting?.trend_email_setting?.emails?.length
					? res.setting.trend_email_setting.emails
					: []
				this.user_setting.cerberus_default_filters = res?.setting?.cerberus_default_filters
					? res.setting.cerberus_default_filters
					: {}
				this.user_setting.cerberus_column_config = res?.setting?.cerberus_column_config
					? res.setting.cerberus_column_config
					: {}
				this.user_setting.litmus_project_types = migrateLitmusProjectTypes(res?.setting?.litmus_project_types)
				this.user_setting.litmus_cost_parameters = {
					...DEFAULT_LITMUS_COST_PARAMETERS,
					...res?.setting?.litmus_cost_parameters,
				}
				this.user_setting.litmus_metric_selections = res?.setting?.litmus_metric_selections ?? {}

				if (!this.user_setting.emails.length && this.me?.email) {
					this.user_setting.emails.push(this.me.email)
				}

				this.user_settings_fetched_at_by_key.set(key, Date.now())
			})
			.catch((err: Error) => console.error(err?.message === '[object Object]' ? JSON.stringify(err) : err))
			.finally(() => {
				this.user_setting_loading = false
				this.user_settings_promises.delete(key)
				m.redraw()
			})

		this.user_settings_promises.set(key, promise)

		return await promise
	}

	fetchMe = async (customer_id?: string, options: { force?: boolean } = {}) => {
		const { force = false } = options
		const key = this.getCacheKey(customer_id)

		if (!force && this.me && this.isFresh(this.me_fetched_at_by_key.get(key))) {
			return this.me
		}

		if (!force && this.me_promises.has(key)) {
			return await this.me_promises.get(key)
		}

		const promise = client('/api/v1/pages/permissions/me', { params: { customer_id } })
			.then((res: MeResponse) => {
				this.me = res
				this.me_fetched_at_by_key.set(key, Date.now())
				const { functional_role, customer_ids, region_ids } = res
				this.permission = functional_role ?? null
				// BEGIN - LEGACY
				this.available_modes = [
					this.checkPermission(USER_MANAGEMENT_SOLUTION_TYPE.SIGNAL) ? 'sigops' : null,
					// this.checkPermission(USER_MANAGEMENT_SOLUTION_TYPE.SAFETY) ? 'safety' : null,
					this.checkPermission(USER_MANAGEMENT_SOLUTION_TYPE.MOBILITY) ? 'mobility' : null,
				].filter((x): x is AppMode => x !== null)
				// END - LEGACY

				try {
					window.posthog?.identify(res?.user_id, {
						email: res?.email,
						name: `${res?.given_name} ${res?.family_name}`,
						customer_ids: customer_ids,
					})
				} catch (error) {
					console.error((error as Error)?.message === '[object Object]' ? JSON.stringify(error) : error)
				}

				if (!functional_role || !customer_ids?.length || !region_ids?.length) {
					return router.routes.access_denied.navigate()
				}
				if (functional_role) {
					this.is_admin =
						functional_role?.role === USER_MANAGEMENT_ROLE_TYPE.ADMIN ||
						functional_role?.role === USER_MANAGEMENT_ROLE_TYPE.SUPER_ADMIN
					this.is_super_admin = functional_role?.role === USER_MANAGEMENT_ROLE_TYPE.SUPER_ADMIN
				}
				this.has_api_access = this.is_super_admin || !!res.api_access
				this.has_agent_access = this.is_super_admin || !!res.agent_access

				return res
			})
			.catch((err: Error) => console.error(err?.message === '[object Object]' ? JSON.stringify(err) : err))
			.finally(() => {
				this.permission_loading = false
				this.me_promises.delete(key)
			})

		this.me_promises.set(key, promise)

		return await promise
	}

	update = async (data: { title?: string; last_name?: string; first_name?: string }) =>
		await client('/api/v1/pages/users/me', {
			method: 'PUT',
			body: {
				title: data?.title,
				family_name: data?.last_name,
				given_name: data?.first_name,
			},
		})

	getDefaultViewOptions = async (customer_id?: string) => {
		this.loading_permitted_regions = true
		const params: Record<string, string> = {}
		if (customer_id) {
			params.customer_id = customer_id
		}
		return client('/api/v1/pages/permitted-regions', { params })
			.then(async (res: PermittedRegion[]) => {
				const region: PermittedRegion[] = []
				const subregion: unknown[] = []
				const network: unknown[] = []

				await res?.map((r) => {
					r?.subregions?.map((s) => {
						subregion.push(s)
					})

					r?.networks?.map((n) => {
						network.push(n)
					})

					region.push(r)
				})

				this.permitted_regions = { region, subregion, network }
			})
			.catch((err: Error) => console.error(err?.message === '[object Object]' ? JSON.stringify(err) : err))
			.finally(() => {
				this.loading_permitted_regions = false
			})
	}

	updateUserPreferences = async (body: unknown) => {
		if (this.user_preferences) {
			return await client('/api/v1/pages/user-managements/user-preferences/me', {
				method: 'PUT',
				body,
			}).then(() => notifications.addSuccess('Update Default View success'))
		}

		return await client('/api/v1/pages/user-managements/user-preferences', {
			method: 'POST',
			body,
		}).then(() => notifications.addSuccess('Update Default View success'))
	}

	checkPermission = (
		solution?: string,
		module?: string,
		feature?: string,
	): FunctionalFeature | FunctionalModule | FunctionalSolution | false => {
		const permission = this.permission

		if (solution && module && feature) {
			const functional_solution = permission?.functional_solutions?.find((i) => i.name_slug === solution)
			const functional_module = functional_solution?.functional_modules?.find((i) => i.name_slug === module)
			return functional_module?.functional_features?.find((i) => i.name_slug === feature) ?? false
		}

		if (solution && module) {
			const functional_solution = permission?.functional_solutions?.find((i) => i.name_slug === solution)
			return functional_solution?.functional_modules?.find((i) => i.name_slug === module) ?? false
		}

		if (solution) {
			return permission?.functional_solutions?.find((i) => i.name_slug === solution) ?? false
		}

		return false
	}

	checkCerberusPermission = () =>
		this.checkPermission(USER_MANAGEMENT_SOLUTION_TYPE.SIGNAL, USER_MANAGEMENT_MODULE_TYPE.CERBERUS) ||
		this.checkPermission(USER_MANAGEMENT_SOLUTION_TYPE.SAFETY, USER_MANAGEMENT_MODULE_TYPE.CERBERUS) ||
		this.checkPermission(USER_MANAGEMENT_SOLUTION_TYPE.MOBILITY, USER_MANAGEMENT_MODULE_TYPE.CERBERUS) ||
		this.checkPermission(USER_MANAGEMENT_SOLUTION_TYPE.ENVIRONMENTAL, USER_MANAGEMENT_MODULE_TYPE.CERBERUS)

	checkReportsPermission = () => this.checkODReportsPermission() || this.checkLitmusReportsViewPermission()

	checkODReportsPermission = () =>
		this.checkPermission(USER_MANAGEMENT_SOLUTION_TYPE.ORIGIN_DESTINATION, USER_MANAGEMENT_MODULE_TYPE.OD_REPORTS)

	checkLitmusReportsViewPermission = () =>
		this.checkPermission(USER_MANAGEMENT_SOLUTION_TYPE.SIGNAL, USER_MANAGEMENT_MODULE_TYPE.LITMUS) ||
		this.checkPermission(USER_MANAGEMENT_SOLUTION_TYPE.SAFETY, USER_MANAGEMENT_MODULE_TYPE.LITMUS) ||
		this.checkPermission(USER_MANAGEMENT_SOLUTION_TYPE.MOBILITY, USER_MANAGEMENT_MODULE_TYPE.LITMUS)

	// A litmus report belongs to exactly one solution (its report_metadata.mode), so edit/delete
	// permission must be checked against that one solution rather than OR'd across all of them —
	// otherwise a role with edit/delete access in only one solution could mutate every solution's reports.
	checkLitmusReportsEditPermission = (mode: string) =>
		!!this.checkPermission(
			LITMUS_MODE_TO_SOLUTION[mode],
			USER_MANAGEMENT_MODULE_TYPE.LITMUS,
			USER_MANAGEMENT_FEATURE_TYPE.LITMUS_REPORTS_EDIT,
		)

	checkLitmusReportsDeletePermission = (mode: string) =>
		!!this.checkPermission(
			LITMUS_MODE_TO_SOLUTION[mode],
			USER_MANAGEMENT_MODULE_TYPE.LITMUS,
			USER_MANAGEMENT_FEATURE_TYPE.LITMUS_REPORTS_DELETE,
		)

	checkRealTimePermission = () => {
		const current_route = router.currentRoute()
		const key = current_route?.name
		const mode = current_route?.query?.mode ?? ''

		if (
			['mobility_region', 'mobility_subregion', 'mobility_network'].find((i) => i === key) ||
			(current_route.name === 'roadway' && current_route.query.mode === 'mobility') ||
			(mode === 'mobility' && key === 'system')
		) {
			return this.checkPermission(USER_MANAGEMENT_SOLUTION_TYPE.MOBILITY, USER_MANAGEMENT_MODULE_TYPE.REAL_TIME)
		}

		if (
			[
				'safety_region',
				'safety_subregion',
				'safety_network',
				'safety_intersection',
				'safety_signal',
				'safety_phase',
				'roadway',
			].find((i) => i === key) ||
			(mode === 'safety' && key === 'system')
		) {
			return this.checkPermission(USER_MANAGEMENT_SOLUTION_TYPE.SAFETY, USER_MANAGEMENT_MODULE_TYPE.REAL_TIME)
		}

		if (
			[
				'sigops_region',
				'sigops_subregion',
				'sigops_network',
				'sigops_intersection',
				'sigops_signal',
				'sigops_phase',
			].find((i) => i === key) ||
			(mode === 'sigops' && key === 'system')
		) {
			return this.checkPermission(USER_MANAGEMENT_SOLUTION_TYPE.SIGNAL, USER_MANAGEMENT_MODULE_TYPE.REAL_TIME)
		}

		return false
	}

	// TODO: user_preferences.default_mode must include scoping by customer before we can move off of CUSTOMER_MODE_DEFAULTS
	getDefaultPermissions = (customer_id?: string) => {
		if (!customer_id) return

		const name_to_key: Record<string, AppMode> = {
			'Signal Operations': 'sigops',
			Safety: 'safety',
			Mobility: 'mobility',
		}
		// BEGIN - TODO: Deprecated? Should prefer checkPermissions to set this.available_modes?
		if (!this.available_modes) {
			let available_modes: AppMode[] = []
			if (customer_id) {
				const enabled_modes = CUSTOMER_MODE_DEFAULTS.filter(
					(x: { id: string }) => x.id === maximizeID(customer_id),
				)?.[0]?.enabled_modes
				available_modes = enabled_modes?.map((x: string) => name_to_key[x]).filter(Boolean) || []
			}
			this.available_modes = available_modes
		}
		// END - TODO: Deprecated? Should prefer checkPermissions to set this.available_modes?

		const default_mode_name = CUSTOMER_MODE_DEFAULTS.filter(
			(x: { id: string }) => x.id === maximizeID(customer_id),
		)?.[0]?.default_mode
		this.default_mode = name_to_key[default_mode_name] ?? null
	}

	getRole = () => this?.permission?.role

	updateUserSetting = async (
		body: UpdateUserSettingBody,
		params: { isDeleting?: boolean; enableSaveNotify?: boolean } = {},
	) => {
		const { isDeleting = false, enableSaveNotify = true } = params

		this.user_setting_processing = true

		const url = this.user_setting?.id
			? `/api/v1/pages/user-managements/user-settings/${this.user_setting.id}`
			: '/api/v1/pages/user-managements/user-settings'

		const method = this.user_setting?.id ? 'PUT' : 'POST'

		const {
			id,
			date_ranges,
			cerberus_default_filters,
			litmus_project_types,
			litmus_cost_parameters,
			litmus_metric_selections,
			...filteredUserSetting
		} = this.user_setting || {}

		const payload = {
			customer_id: body?.customer_id,
			setting: {
				date_ranges: body?.setting?.date_ranges || date_ranges,
				cerberus_default_filters:
					body?.setting?.cerberus_default_filters ?? this.user_setting?.cerberus_default_filters,
				cerberus_column_config: body?.setting?.cerberus_column_config ?? this.user_setting?.cerberus_column_config,
				litmus_project_types: body?.setting?.litmus_project_types ?? litmus_project_types,
				litmus_cost_parameters: body?.setting?.litmus_cost_parameters ?? litmus_cost_parameters,
				litmus_metric_selections: body?.setting?.litmus_metric_selections ?? litmus_metric_selections,
				trend_email_setting: {
					...filteredUserSetting,
					...body?.setting,
				},
			},
		}

		delete (payload.setting.trend_email_setting as Record<string, unknown>).date_ranges
		delete (payload.setting.trend_email_setting as Record<string, unknown>).cerberus_default_filters
		delete (payload.setting.trend_email_setting as Record<string, unknown>).cerberus_column_config
		delete (payload.setting.trend_email_setting as Record<string, unknown>).litmus_project_types
		delete (payload.setting.trend_email_setting as Record<string, unknown>).litmus_cost_parameters
		delete (payload.setting.trend_email_setting as Record<string, unknown>).litmus_metric_selections

		return client(url, {
			method,
			body: payload,
		}).then((res: UserSettingResponse) => {
			if (!isDeleting && enableSaveNotify) {
				notifications.addSuccess('Settings saved')
			}

			if (res?.id) {
				this.user_setting.id = res.id
			}

			this.user_setting.date_ranges = res?.setting?.date_ranges ?? []
			this.user_setting.frequency = res?.setting?.trend_email_setting?.frequency ?? { ...getFrequencyDefault() }
			this.user_setting.emails = res?.setting?.trend_email_setting?.emails ?? []
			this.user_setting.failed_emails = res?.setting?.trend_email_setting?.failed_emails ?? []
			this.user_setting.cerberus_default_filters = res?.setting?.cerberus_default_filters ?? {}
			this.user_setting.cerberus_column_config = res?.setting?.cerberus_column_config ?? {}
			this.user_setting.litmus_project_types = migrateLitmusProjectTypes(res?.setting?.litmus_project_types)
			this.user_setting.litmus_cost_parameters = {
				...DEFAULT_LITMUS_COST_PARAMETERS,
				...res?.setting?.litmus_cost_parameters,
			}
			this.user_setting.litmus_metric_selections = res?.setting?.litmus_metric_selections ?? {}

			return res
		})
		.finally(() => {
			this.user_setting_processing = false
		})
	}

	resetFailedEmails = () => {
		this.user_setting.failed_emails = []
	}

	hasMetricAvailability = (metric_type: string): boolean => {
		return this.me?.metrics_availability[metric_type] ?? false
	}

	hasFeatureFlag = (feature_flag_name: string) => {
		return this.me?.feature_flags.includes(feature_flag_name) ?? false
	}
}

export default new Cache()
